import React, { createContext, useContext, useState, useEffect } from 'react';
import { User as FirebaseUser } from 'firebase/auth';
import { authService, UserData } from '../lib/firebase';
// Removed local auth dependency for production stability
import { toast } from 'react-hot-toast';
import { secureLogger } from '@/utils/secure-logger';
import { ENV } from '@/config/env';
import type { CheckoutStatusResponse } from '@/types/api';
interface AuthContextType {
  user: UserData | null;
  firebaseUser: FirebaseUser | null;
  loading: boolean;
  activeProducts: string[];
  activeProductsLoading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  register: (userData: RegisterData) => Promise<{ success: boolean; error?: string }>;
  loginWithGoogle: () => Promise<{ success: boolean; error?: string }>;
  loginWithFacebook: () => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  updateUser: (userData: Partial<UserData>) => Promise<void>;
  resetPassword: (email: string) => Promise<{ success: boolean; error?: string }>;
  isAuthenticated: () => boolean;
  hasRole: (role: string) => boolean;
  hasUserTier: (tier: string) => boolean;
  isFreeUser: () => boolean;
  isPremiumUser: () => boolean;
  canAccessFeature: (feature: string) => boolean;
}
interface RegisterData {
  email: string;
  password: string;
  name: string;
  phone?: string;
  company?: string;
  userTier?: 'free' | 'starter' | 'professional' | 'enterprise';
  role?: 'owner' | 'admin' | 'agent' | 'backoffice' | 'investor';
}
const AuthContext = createContext<AuthContextType | null>(null);
export function FirebaseAuthProvider(props: { children?: React.ReactNode }) {
  // Safe destructuring to avoid null errors
  const children = props?.children || null;
  const [user, setUser] = useState<UserData | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeProducts, setActiveProducts] = useState<string[]>([]);
  const [activeProductsLoading, setActiveProductsLoading] = useState(false);

  // Fetch active products from backend (SQLite via Cloudflare Workers)
  const fetchActiveProducts = async (userId: string, idToken: string) => {
    setActiveProductsLoading(true);
    try {
      // Rota correta: /api/checkout/status (sem userId na URL, pega do token)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      const response = await fetch(`${ENV.API_URL}/api/checkout/status`, {
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (response.ok) {
        const data = (await response.json()) as CheckoutStatusResponse;
        setActiveProducts(data.activeProducts || []);
        console.log('✅ Active products loaded:', data.activeProducts);
      } else {
        console.warn('⚠️ Failed to load active products:', response.status);
        setActiveProducts([]);
      }
    } catch (error) {
      console.warn('⚠️ Error loading active products:', error);
      setActiveProducts([]);
    } finally {
      setActiveProductsLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;

    const unsubscribe = authService.onAuthStateChanged(async firebaseUser => {
      if (!mounted) return;

      setLoading(true);

      if (firebaseUser) {
        try {
          // Get user data from Firestore with timeout (5s optimized)
          const userDataPromise = authService.getUserData(firebaseUser.uid);
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Firestore timeout')), 5000)
          );

          const userData = (await Promise.race([
            userDataPromise,
            timeoutPromise,
          ])) as UserData | null;

          if (!mounted) return;

          if (userData) {
            // Fetch organizador plan from backend with timeout
            try {
              // ✅ forceRefresh=true para garantir token válido (renova se expirado)
              const token = await firebaseUser.getIdToken(true);

              const apiUrl = import.meta.env.VITE_API_URL || 'https://api.chatbotimoveis.com.br';

              // Create abort controller with 5s timeout
              const controller = new AbortController();
              const timeoutId = setTimeout(() => controller.abort(), 5000);

              const response = await fetch(`${apiUrl}/api/organizador-clientes/me`, {
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
                signal: controller.signal,
              });

              clearTimeout(timeoutId);

              if (response.ok) {
                const meData = await response.json();
                if (meData.user?.plan) {
                  userData.organizadorPlan = meData.user.plan;
                }
              }
              // ✅ ROBUSTEZ: Erros esperados são silenciados, mas inesperados são logados
            } catch (error: any) {
              // Erros esperados (não logam):
              // - AbortError: Timeout de 5s
              // - 401: Backend ainda não configurou o usuário
              const isExpectedError = error.name === 'AbortError' || error.status === 401;

              if (!isExpectedError) {
                // Erros inesperados: log em DEV para debugging
                if (import.meta.env.DEV) {
                  console.warn('⚠️ Could not fetch organizador plan (plan will default to free):', {
                    error: error.message,
                    status: error.status,
                    name: error.name,
                  });
                }
              }
              // Plan permanece 'organizador_gratuito' (default seguro)
            }

            // Fetch active products from backend (SQLite)
            try {
              const token = await firebaseUser.getIdToken();
              await fetchActiveProducts(firebaseUser.uid, token);
            } catch (error) {
              console.warn('⚠️ Could not fetch active products:', error);
              // Continue without active products
            }

            setUser(userData);
            setFirebaseUser(firebaseUser);
            localStorage.setItem('userData', JSON.stringify(userData));
          } else {
            const defaultUserData: UserData = {
              uid: firebaseUser.uid,
              email: firebaseUser.email || '',
              name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
              userTier: 'free',
              subscriptionStatus: 'trial',
              role: 'agent',
              createdAt: new Date(),
              lastLogin: new Date(),
              profileCompleted: false,
            };

            try {
              await Promise.race([
                authService.updateUserData(firebaseUser.uid, defaultUserData),
                new Promise<void>((_, reject) => setTimeout(() => reject(new Error('updateUserData timeout')), 5000)),
              ]);
            } catch (error) {
              // ⚠️ ROBUSTEZ: Log do erro em DEV, mas não quebrar a aplicação
              if (import.meta.env.DEV) {
                console.error('❌ Failed to update user data in Firestore:', error);
              }
              // Usuário ainda será criado em localStorage e state
            }

            setUser(defaultUserData);
            setFirebaseUser(firebaseUser);
            localStorage.setItem(
              'firebaseUser',
              JSON.stringify({
                uid: firebaseUser.uid,
                email: firebaseUser.email,
                displayName: firebaseUser.displayName,
              })
            );
            localStorage.setItem('userData', JSON.stringify(defaultUserData));
          }
        } catch (error) {
          if (!mounted) return;

          const defaultUserData: UserData = {
            uid: firebaseUser.uid,
            email: firebaseUser.email || '',
            name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
            userTier: 'free',
            subscriptionStatus: 'trial',
            role: 'agent',
            createdAt: new Date(),
            profileCompleted: false,
          };
          setUser(defaultUserData);
          setFirebaseUser(firebaseUser);
          localStorage.setItem(
            'firebaseUser',
            JSON.stringify({
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              displayName: firebaseUser.displayName,
            })
          );
          localStorage.setItem('userData', JSON.stringify(defaultUserData));
        }
      } else {
        if (mounted) {
          setUser(null);
          setFirebaseUser(null);
          localStorage.removeItem('firebaseUser');
          localStorage.removeItem('userData');
        }
      }

      if (mounted) {
        setLoading(false);
      }
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);
  const login = async (email: string, password: string) => {
    try {
      setLoading(true);

      const result = (await authService.signIn(email, password)) as any;
      toast.success('✅ Login feito! Bem-vindo de volta!');
      return { success: true };
    } catch (error: any) {
      secureLogger.security('Login attempt failed', {
        component: 'FirebaseAuthContext',
        action: 'login',
        errorCode: error.code,
      });

      let errorMessage = 'Não conseguimos fazer o login agora. Tente novamente.';

      // Mapear erros do Firebase
      if (error.code === 'auth/invalid-credential' || error.code === 'auth/user-not-found') {
        errorMessage = '❌ Email ou senha não confere. Verifique os dados e tente de novo.';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = '❌ Senha incorreta. Tente novamente.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = '⚠️ Muitas tentativas. Aguarde alguns minutos e tente novamente.';
      } else if (error.message?.includes('400') || error.message?.includes('Bad Request')) {
        errorMessage =
          '⚙️ Sistema temporariamente indisponível. Tente novamente em alguns minutos.';
      }

      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };
  const register = async (userData: RegisterData) => {
    try {
      setLoading(true);

      const result = await authService.createUser(userData.email, userData.password, {
        name: userData.name,
        phone: userData.phone,
        company: userData.company,
        userTier: userData.userTier || 'free',
        role: userData.role || 'agent',
        subscriptionStatus: 'trial',
      });
      toast.success('🎉 Sua conta foi criada! Bem-vindo ao Chatbot Imóveis!');
      return { success: true };
    } catch (error: any) {
      secureLogger.security('Registration attempt failed', {
        component: 'FirebaseAuthContext',
        action: 'register',
        errorCode: error.code,
      });

      let errorMessage = 'Não conseguimos criar sua conta agora. Tente novamente.';

      // Mapear erros do Firebase
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = '📧 Este email já tem uma conta. Tente fazer login ou use outro email.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = '🔒 Senha muito fraca. Use pelo menos 6 caracteres.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = '📧 Email inválido. Verifique e tente novamente.';
      } else if (error.code === 'auth/operation-not-allowed') {
        errorMessage = '⚠️ Cadastro temporariamente indisponível. Tente novamente mais tarde.';
      }

      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };
  const logout = async () => {
    try {
      await authService.signOut();

      // LIMPAR DADOS DO LOCALSTORAGE
      localStorage.removeItem('firebase_token'); // Limpeza legado
      localStorage.removeItem('firebase_user'); // Limpeza legado
      localStorage.removeItem('firebaseUser'); // Limpeza legado
      localStorage.removeItem('userData'); // Dado atual

      toast.success('Logout realizado com sucesso');
    } catch (error) {
      secureLogger.error('Logout failed', error, {
        component: 'FirebaseAuthContext',
        action: 'logout',
      });
      toast.error('Erro ao fazer logout');
    }
  };
  const updateUser = async (updates: Partial<UserData>) => {
    if (!user) return;
    try {
      await authService.updateUserData(user.uid, updates);
      // Update local state
      const updatedUser = { ...user, ...updates };
      setUser(updatedUser);
      localStorage.setItem('userData', JSON.stringify(updatedUser));
      toast.success('Perfil atualizado com sucesso');
    } catch (error) {
      secureLogger.error('User update failed', error, {
        component: 'FirebaseAuthContext',
        action: 'updateUser',
      });
      toast.error('Erro ao atualizar perfil');
      throw error;
    }
  };
  const loginWithGoogle = async () => {
    try {
      setLoading(true);
      const result = (await authService.signInWithGoogle()) as any;
      toast.success('Login com Google realizado com sucesso!');
      return { success: true };
    } catch (error: any) {
      secureLogger.security('Google login attempt failed', {
        component: 'FirebaseAuthContext',
        action: 'googleLogin',
        errorCode: error.code,
      });
      let errorMessage = 'Erro ao fazer login com Google';
      switch (error.code) {
        case 'auth/popup-closed-by-user':
          errorMessage = 'Login cancelado pelo usuário';
          break;
        case 'auth/popup-blocked':
          errorMessage = 'Popup bloqueado pelo navegador';
          break;
        case 'auth/account-exists-with-different-credential':
          errorMessage = 'Conta já existe com credencial diferente';
          break;
        default:
          errorMessage = error.message || 'Erro ao fazer login com Google';
      }
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };
  const loginWithFacebook = async () => {
    try {
      setLoading(true);
      // TODO: Implement Facebook login when needed
      toast.error('Login com Facebook não está disponível no momento');
      return { success: false };
    } catch (error: any) {
      secureLogger.security('Facebook login attempt failed', {
        component: 'FirebaseAuthContext',
        action: 'facebookLogin',
        errorCode: error.code,
      });
      let errorMessage = 'Erro ao fazer login com Facebook';
      switch (error.code) {
        case 'auth/popup-closed-by-user':
          errorMessage = 'Login cancelado pelo usuário';
          break;
        case 'auth/popup-blocked':
          errorMessage = 'Popup bloqueado pelo navegador';
          break;
        case 'auth/account-exists-with-different-credential':
          errorMessage = 'Conta já existe com credencial diferente';
          break;
        default:
          errorMessage = error.message || 'Erro ao fazer login com Facebook';
      }
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };
  const resetPassword = async (email: string) => {
    try {
      await authService.resetPassword(email);
      toast.success('Email de recuperação enviado!');
      return { success: true };
    } catch (error: any) {
      secureLogger.security('Password reset attempt failed', {
        component: 'FirebaseAuthContext',
        action: 'resetPassword',
        errorCode: error.code,
      });
      let errorMessage = 'Erro ao enviar email de recuperação';
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'Usuário não encontrado';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Email inválido';
          break;
        default:
          errorMessage = error.message || 'Erro ao enviar email de recuperação';
      }
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };
  const isAuthenticated = () => {
    return !!user && !!firebaseUser;
  };
  const hasRole = (role: string) => {
    return user?.role === role;
  };
  const hasUserTier = (tier: string) => {
    return user?.userTier === tier;
  };
  const isFreeUser = () => {
    return user?.userTier === 'free';
  };
  const isPremiumUser = () => {
    return user?.userTier !== 'free';
  };
  const canAccessFeature = (feature: string) => {
    if (!user) return false;
    // Feature access based on user tier
    const tierFeatures = {
      free: ['leadManagerFree', 'organizadorClientes', 'basicChat', 'profile', 'settings'],
      starter: [
        'leadManagerFree',
        'leadManagerPro',
        'followupAI',
        'basicAnalytics',
        'profile',
        'settings',
      ],
      professional: [
        'leadManagerFree',
        'leadManagerPro',
        'followupAI',
        'basicAnalytics',
        'aiAgents',
        'digitalContracts',
        'leadInsights',
        'advancedAnalytics',
        'profile',
        'settings',
        'team',
      ],
      enterprise: [
        'leadManagerFree',
        'leadManagerPro',
        'followupAI',
        'basicAnalytics',
        'aiAgents',
        'digitalContracts',
        'leadInsights',
        'advancedAnalytics',
        'smartPropertySite',
        'marketplaceHub',
        'apiAccess',
        'whiteLabel',
        'profile',
        'settings',
        'team',
        'billing',
      ],
    };
    const allowedFeatures = tierFeatures[user.userTier] || tierFeatures.free;
    return allowedFeatures.includes(feature);
  };
  const value: AuthContextType = {
    user,
    firebaseUser,
    loading,
    activeProducts,
    activeProductsLoading,
    login,
    register,
    loginWithGoogle,
    loginWithFacebook,
    logout,
    updateUser,
    resetPassword,
    isAuthenticated,
    hasRole,
    hasUserTier,
    isFreeUser,
    isPremiumUser,
    canAccessFeature,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
export function useFirebaseAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useFirebaseAuth must be used within a FirebaseAuthProvider');
  }
  return context;
}
// Deprecated: Use useFirebaseAuth instead
export const useAuth = useFirebaseAuth;
